import {
    Component,
    OnInit,
    Input,
    OnDestroy,
    Output,
    EventEmitter,
    ViewChild,
    ElementRef,
    Inject,
    PLATFORM_ID
} from '@angular/core';
import {FormBuilder, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {Observable, Subject} from 'rxjs';
import {StorageState} from '@store/state/storage.state';
import {Store} from '@ngrx/store';
import {AppState} from '@store/state/app.state';
import {CustomerService} from '@iw-shop/customer/services/customer.service';
import {
    setMappedCustomersSuccess
} from '@store/actions/user.action';
import {MappedCustomer} from '@store/state/user.state';
import {CustomerAddress} from '@iw-shop/customer/interfaces/customer';
import {v4 as uuidv4} from 'uuid';
import {ApiCustomersSetRequest} from '@api/customers';
import {FormStatus} from '@iw-shop/forms/components/submit-button/submit-button.component';
import {login} from '@store/actions/authentication.action';
import {take} from 'rxjs/operators';
import {
    getSelectedBillingAddress,
    getSelectedCustomer,
    getSelectedDeliveryAddress
} from '@store/selectors/user.selector';
import {faCalendar} from "@fortawesome/free-regular-svg-icons";
import {setSelectedBillingAddress, setSelectedDeliveryAddress} from "@store/actions/storage.action";
import {DeliveryAreaService} from "@iw-shop/delivery-area/services/delivery-area.service";
import {ConfigState} from "@store/state/config.state";
import {GoogleTagManagerService} from "@iw-shop/google-tag-manager/services/google-tag-manager.service";
import {DeliveryTourService} from "@iw-shop/delivery-tour/services/delivery-tour.service";
import {ConfigService} from "@iw-shop/config/service/config.service";
import {isPlatformBrowser} from "@angular/common";

@Component({
    selector: 'iw-shop-customer-register',
    standalone: false,
    templateUrl: './customer-register.component.html',
    styleUrls: ['./customer-register.component.scss']
})
export class CustomerRegisterComponent implements OnInit, OnDestroy {
    @ViewChild('datePicker') datePicker!: ElementRef;
    @ViewChild('myFormReference') myFormReference!: ElementRef;

    @Input() guestAvailable: boolean = false;
    @Input() headline: string | null = 'Ich bin Neukunde';
    @Input() submitLabel: string = 'Registrieren';
    @Input() isGuest: boolean = false;
    @Input() fillFromState: boolean = false;

    @Output() onLoginSuccess: EventEmitter<any> = new EventEmitter();
    @Output() onRegisterSuccess: EventEmitter<any> = new EventEmitter();

    protected readonly isBrowser: boolean;

    destroy$: Subject<boolean> = new Subject<boolean>();

    apiError?: {
        notification: string,
        errors?: { key: string, error: string }[]
    };

    captcha: boolean = false;

    isDeliveryAddress: boolean = false;

    storage$: Observable<StorageState>;
    config$: Observable<ConfigState>;

    form: FormGroup;

    dateOfBirthMin: Date;
    dateOfBirthMax: Date;

    formStatus: FormStatus = null;

    countries: string[] = [];
    deliveryAreas: { postcode: string, town: string }[] = [];


    constructor(
        protected fb: FormBuilder,
        protected store: Store<AppState>,
        protected customerService: CustomerService,
        protected configService: ConfigService,
        protected deliveryAreaService: DeliveryAreaService,
        public deliveryTourService: DeliveryTourService,
        protected googleTagManagerService: GoogleTagManagerService,
        @Inject(PLATFORM_ID) platformId: Object

    ) {
        this.isBrowser = isPlatformBrowser(platformId);
        this.form =  this.fb.group({
            address: this.fb.group({
                salutation: [''],
                firstName: [''],
                name: [''],
                company: [''],
                street: [''],
                postcode: [''],
                town: [''],
                phone: [''],
                floor: [0],
                elevator: [null],
                country: [null],

            }),
            deliveryAddress: this.fb.group({
                salutation: [''],
                firstName: [''],
                name: [''],
                company: [''],
                street: [''],
                postcode: [''],
                town: [''],
                phone: [''],
                floor: [0],
                elevator: [null],
                country: [null]
            }),
            dateOfBirth: this.fb.control(''),
            email: ['', [Validators.required, Validators.email]],
            password: ['', [Validators.required, Validators.minLength(6)]],
            terms: [false, [Validators.requiredTrue]],
            guest: [false],
            dynamicArticleIds: this.fb.control([])
        });

        const year = new Date().getFullYear();
        this.dateOfBirthMax = new Date(year - 18, 0, 0);
        this.dateOfBirthMin = new Date(year - 120, 0, 0);

        this.storage$ = this.store.select(state => state.storage);
        this.config$ = this.store.select(state => state.config);
        this.store.select(state => state.config)
            .pipe(
                take(1)
            )
            .subscribe(
                c => {

                    if (c.countries) {
                        this.countries = c.countries;
                    }
                    if (c.defaultCountry) {
                        this.form.get('address.country')?.setValue(c.defaultCountry)
                        this.form.get('deliveryAddress.country')?.setValue(c.defaultCountry)
                    }

                    if (c.customerShopFields) {
                        c.customerShopFields.map(r => {
                            let validators: ValidatorFn[] = [];
                            if (r.field == 'email') {
                                return;
                            }
                            if (r.field == 'country' &&
                                this.countries.length == 0) {
                                return;
                            }
                            if (r.required) {
                                validators.push(Validators.required);
                            }

                            if (r.regex) {
                                validators.push(Validators.pattern(new RegExp(r.regex)))
                            }
                            if (this.form.get(r.field)) {
                                if (r.disabled) {
                                    this.form.get(r.field)?.disable()
                                }
                                this.form.get(r.field)?.setValidators(validators);
                                this.form.get(r.field)?.updateValueAndValidity();
                            }
                            if (this.form.get('address.' + r.field)) {
                                this.form.get('address.' + r.field)?.setValidators(validators);
                                this.form.get('address.' + r.field)?.updateValueAndValidity();
                            }

                        });
                    }

                }
            );


    }

    ngOnInit(): void {
        this.deliveryAreaService.get({
            deliveryAreas: {
                sort: [{field: 'postcode', direction: 1}],
                limit: 500
            }
        }).subscribe(
            r => {
                if (r.result?.deliveryAreas.data) {
                    this.deliveryAreas = r.result.deliveryAreas.data.map(d => ({
                        ...d,
                        // Hier werden die Felder direkt für die gesamte Lebensdauer der Komponente gesäubert
                        town: d.town?.trim(),
                        postcode: d.postcode?.trim()
                    }));
                }
            }
        );

        if (this.fillFromState) {
            this.store.select(getSelectedCustomer)
                .pipe(take(1))
                .subscribe(
                    r => {
                        if (r) {
                            this.setGuest(r.guest);
                            this.form.get('email')?.setValue(r.email);
                        }
                    }
                );

            // billingAddress
            this.store.select(getSelectedBillingAddress)
                .pipe(take(1))
                .subscribe(
                    r => {
                        if (r) {
                            this.form.get('address.salutation')?.setValue(r.salutation);
                            this.form.get('address.firstName')?.setValue(r.firstName);
                            this.form.get('address.name')?.setValue(r.name);
                            this.form.get('address.company')?.setValue(r.company);
                            this.form.get('address.street')?.setValue(r.street);
                            this.form.get('address.postcode')?.setValue(r.postcode);
                            this.form.get('address.town')?.setValue(r.town);
                            this.form.get('address.floor')?.setValue(r.floor);
                            this.form.get('address.elevator')?.setValue(r.elevator);
                            this.form.get('address.phone')?.setValue(r.phone);
                            this.form.get('address.country')?.setValue(r.country);
                        }
                    }
                );

            // deliveryAddress
            this.store.select(getSelectedDeliveryAddress)
                .pipe(take(1))
                .subscribe(
                    r => {
                        if (r) {
                            this.isDeliveryAddress = true;
                            this.setDeliveryAddressValidators(true);
                            this.form.get('deliveryAddress.salutation')?.setValue(r.salutation);
                            this.form.get('deliveryAddress.firstName')?.setValue(r.firstName);
                            this.form.get('deliveryAddress.name')?.setValue(r.name);
                            this.form.get('deliveryAddress.company')?.setValue(r.company);
                            this.form.get('deliveryAddress.street')?.setValue(r.street);
                            this.form.get('deliveryAddress.postcode')?.setValue(r.postcode);
                            this.form.get('deliveryAddress.town')?.setValue(r.town);
                            this.form.get('deliveryAddress.floor')?.setValue(r.floor);
                            this.form.get('deliveryAddress.elevator')?.setValue(r.elevator);
                            this.form.get('deliveryAddress.phone')?.setValue(r.phone);
                            this.form.get('deliveryAddress.country')?.setValue(r.country);

                        }
                    }
                );
        }

    }

    selectYear(){
        if(this.isBrowser &&
            this.form.get('dateOfBirth')?.value == '' ||
            this.form.get('dateOfBirth')?.value == null){
            setTimeout(() => {
                // 2. Find and click the year button
                const btns = document.querySelectorAll('button.p-datepicker-select-year');
                btns.forEach(btn => {
                    (btn as HTMLButtonElement).click();
                });
            }, 100);
        }
    }
    isRequired(field: string): boolean {
        return this.customerService.isCustomerShopFieldRequired(field);
    }

    isHiddenOrDisabled(field: string): boolean {
        return (this.customerService.isCustomerShopFieldHidden(field) ||
            this.customerService.isCustomerShopFieldDisabled(field));
    }

    getFieldErrorMsg(field: string, defaultErrorMsg: string): string {
        return this.customerService.getCustomerShopFieldErrorMsg(field, defaultErrorMsg);
    }

    isFormRequestError(value: string): boolean {
        let form = this.form?.get(value);
        return !!(form?.errors &&
            this.formStatus == 'submitForm');
    }

    isFormRequestApiError(key: string): boolean {
        return !!this.apiError?.errors?.filter(r => r.key == key)[0];

    }

    setRegisterSuccess() {
        this.formStatus = 'success';
        this.googleTagManagerService.pushTag({event: 'customerNew'});
        setTimeout(() => {
            this.onRegisterSuccess.emit();
        }, 100);
    }

    setLoginSuccess() {
        this.formStatus = 'success'
        setTimeout(() => {
            this.onLoginSuccess.emit();
        }, 100);
    }

    setCaptcha(value: boolean) {
        setTimeout(() => {
            this.captcha = value;
        }, 100);

    }

    setGuest(guest: boolean) {
        this.isGuest = guest;
        this.form.get('guest')?.setValue(guest);
        if (!this.isGuest) {
            this.form.get('password')?.setValidators([Validators.required, Validators.minLength(2)]);
            this.form.get('password')?.updateValueAndValidity();
            this.submitLabel = 'Registrieren';
        } else {
            this.form.get('password')?.setValidators(null);
            this.form.get('password')?.updateValueAndValidity();
            this.submitLabel = 'Weiter als Gast'
        }
    }

    toggleDeliveryAddress() {
        this.isDeliveryAddress = !this.isDeliveryAddress;
        this.setDeliveryAddressValidators(this.isDeliveryAddress);
    }

    setDeliveryAddressValidators(isDeliveryAddressRequired: boolean) {
        if (isDeliveryAddressRequired) {
            this.store.select(state => state.config)
                .pipe(
                    take(1)
                )
                .subscribe(
                    c => {
                        if (c.customerShopFields) {
                            c.customerShopFields.map(r => {
                                let validators: ValidatorFn[] = [];


                                // Gibt es bei der Lieferadresse nicht
                                if (r.field == 'phone') {
                                    return;
                                }
                                if (r.required) {
                                    validators.push(Validators.required);
                                }
                                if (r.regex) {
                                    validators.push(Validators.pattern(new RegExp(r.regex)));
                                }
                                if (this.form.get(r.field)) {
                                    if (r.disabled) {
                                        this.form.get(r.field)?.disable();
                                    }
                                    this.form.get(r.field)?.setValidators(validators);
                                    this.form.get(r.field)?.updateValueAndValidity();
                                }
                                if (this.form.get('deliveryAddress.' + r.field)) {
                                    this.form.get('deliveryAddress.' + r.field)?.setValidators(validators);
                                    this.form.get('deliveryAddress.' + r.field)?.updateValueAndValidity();
                                }

                            });
                        }

                    }
                );
        } else {
            this.form.get('deliveryAddress.salutation')?.setValidators(null);
            this.form.get('deliveryAddress.salutation')?.updateValueAndValidity();
            this.form.get('deliveryAddress.firstName')?.setValidators(null);
            this.form.get('deliveryAddress.firstName')?.updateValueAndValidity();
            this.form.get('deliveryAddress.name')?.setValidators(null);
            this.form.get('deliveryAddress.name')?.updateValueAndValidity();
            this.form.get('deliveryAddress.street')?.setValidators(null);
            this.form.get('deliveryAddress.street')?.updateValueAndValidity();
            this.form.get('deliveryAddress.postcode')?.setValidators(null);
            this.form.get('deliveryAddress.postcode')?.updateValueAndValidity();
            this.form.get('deliveryAddress.town')?.setValidators(null);
            this.form.get('deliveryAddress.town')?.updateValueAndValidity();
            this.form.get('deliveryAddress.floor')?.setValidators(null);
            this.form.get('deliveryAddress.floor')?.updateValueAndValidity();
            this.form.get('deliveryAddress.elevator')?.setValidators(null);
            this.form.get('deliveryAddress.elevator')?.updateValueAndValidity();
            this.form.get('deliveryAddress.country')?.setValidators(null);
            this.form.get('deliveryAddress.country')?.updateValueAndValidity();
        }
    }

    getDeliveryPostcodesGroup(): string[] {
        let postcodes: string[] = [];
        this.deliveryAreas.map(r => {
            if (postcodes.filter(p => p == r.postcode).length == 0) {
                postcodes.push(r.postcode);
            }
        });
        return postcodes;
    }

    getDeliveryTownsByPostcode(postcode: string): string[] {
        return this.deliveryAreas.filter(r => r.postcode == postcode).map(
            r => {
                return r.town;
            }
        );
    }


    register() {
        this.formStatus = 'loading';
        this.apiError = undefined;

        // Liefergebiet ist hinterlegt und es wurde keine Pickup-Tour gewählt
        if (this.deliveryAreas.length > 0 &&
            !this.deliveryTourService.getSelectedDeliveryTour().value?.pickupTour) {
            if (!this.isDeliveryAddress &&
                this.deliveryAreas.filter(d => d.postcode == this.form.get('address.postcode')?.value).length == 0
            ) {
                this.form.get('address.postcode')?.setValue('');
            }
            let townDummy = this.form.get('address.town')?.value;
            let postcode = this.form.get('address.postcode')?.value;
            if (!this.isDeliveryAddress &&
                this.deliveryAreas.filter(d => d.town == this.form.get('address.town')?.value && d.postcode == this.form.get('address.postcode')?.value).length == 0
            ) {
                this.form.get('address.town')?.setValue('');
            }
            if (this.isDeliveryAddress &&
                this.deliveryAreas.filter(d => d.postcode == this.form.get('deliveryAddress.postcode')?.value).length == 0
            ) {
                this.form.get('deliveryAddress.postcode')?.setValue('');
            }
            if (!this.isDeliveryAddress &&
                this.deliveryAreas.filter(d => d.town == this.form.get('deliveryAddress.town')?.value && d.postcode == this.form.get('deliveryAddress.postcode')?.value).length == 0
            ) {
                this.form.get('deliveryAddress.town')?.setValue('');
            }
        }

        /*
        if (!this.form.valid || !this.captcha) {
         */


        if (!this.form.valid || !this.captcha) {
            this.myFormReference.nativeElement.scrollIntoView({behavior: 'smooth'});
            this.formStatus = 'submitForm';
            return;
        }


        let billingAddress: CustomerAddress = {
            addressId: uuidv4(),
            type: '',
            salutation: this.form.get('address.salutation')?.value?.trim() ?? '',
            company: this.form.get('address.company')?.value?.trim(),
            firstName: this.form.get('address.firstName')?.value?.trim(),
            name: this.form.get('address.name')?.value?.trim(),
            country: this.form.get('address.country')?.value?.trim(),
            street: this.form.get('address.street')?.value?.trim(),
            postcode: this.form.get('address.postcode')?.value?.trim(),
            town: this.form.get('address.town')?.value?.trim(),
            phone: this.form.get('address.phone')?.value?.trim()
        };
        if (this.isDeliveryAddress) {
          billingAddress.type = 'billing';
        }
        if (!this.isHiddenOrDisabled('elevator')) {
            billingAddress.elevator = (this.form.get('address.elevator')?.value == 'true' || this.form.get('address.elevator')?.value == true);
        }
        if (!this.isHiddenOrDisabled('floor')) {
            billingAddress.floor = this.form.get('address.floor')?.value;
        }
        if (this.form.get('address.company')?.value
            && this.form.get('address.company')?.value?.trim().length > 0) {
            billingAddress.gesoftName1 = this.form.get('address.company')?.value?.trim();

            billingAddress.gesoftName2 = this.form.get('address.firstName')?.value?.trim() + ' ' + this.form.get('address.name')?.value?.trim();
            if (this.form.get('address.salutation')?.value != '') {
                billingAddress.gesoftName2 = this.form.get('address.salutation')?.value?.trim() + ' ' + billingAddress.gesoftName2;
            }
        } else {
            billingAddress.gesoftName1 = this.form.get('address.firstName')?.value?.trim() + ' ' + this.form.get('address.name')?.value;
            if (this.form.get('address.salutation')?.value != '') {
                billingAddress.gesoftName1 = this.form.get('address.salutation')?.value?.trim() + ' ' + billingAddress.gesoftName1;
            }
            billingAddress.gesoftName2 = '';
        }
        billingAddress.gesoftName1 = (billingAddress.gesoftName1?.length ?? 0) > 100 ? billingAddress.gesoftName1?.substring(0, 100) : billingAddress.gesoftName1 ?? '';
        billingAddress.gesoftName2 = (billingAddress.gesoftName2?.length ?? 0) > 100 ? billingAddress.gesoftName2?.substring(0, 100) : billingAddress.gesoftName2 ?? '';


        let deliveryAddress: CustomerAddress = {
            addressId: uuidv4(),
            type: '',
            salutation: this.form.get('deliveryAddress.salutation')?.value?.trim() ?? '',
            company: this.form.get('deliveryAddress.company')?.value?.trim(),
            firstName: this.form.get('deliveryAddress.firstName')?.value?.trim(),
            name: this.form.get('deliveryAddress.name')?.value?.trim(),
            country: this.form.get('deliveryAddress.country')?.value?.trim(),
            street: this.form.get('deliveryAddress.street')?.value?.trim(),
            postcode: this.form.get('deliveryAddress.postcode')?.value?.trim(),
            town: this.form.get('deliveryAddress.town')?.value?.trim(),
            phone: this.form.get('deliveryAddress.phone')?.value?.trim(),
        };
        if (!this.isHiddenOrDisabled('elevator')) {
            deliveryAddress.elevator = (this.form.get('deliveryAddress.elevator')?.value == 'true' || this.form.get('deliveryAddress.elevator')?.value == true)
        }
        if (!this.isHiddenOrDisabled('floor')) {
            deliveryAddress.floor = this.form.get('deliveryAddress.floor')?.value;
        }
        if (this.form.get('deliveryAddress.company')?.value
            && this.form.get('deliveryAddress.company')?.value.length > 0) {
            deliveryAddress.gesoftName1 = this.form.get('deliveryAddress.company')?.value?.trim();
            deliveryAddress.gesoftName2 = this.form.get('deliveryAddress.firstName')?.value?.trim() + ' ' + this.form.get('deliveryAddress.name')?.value?.trim();
            if (this.form.get('deliveryAddress.salutation')?.value?.trim() != '') {
                deliveryAddress.gesoftName2 = this.form.get('deliveryAddress.salutation')?.value?.trim() + ' ' + deliveryAddress.gesoftName2;
            }
        } else {
            deliveryAddress.gesoftName1 = this.form.get('deliveryAddress.firstName')?.value?.trim() + ' ' + this.form.get('deliveryAddress.name')?.value;
            if (this.form.get('deliveryAddress.salutation')?.value != '') {
                deliveryAddress.gesoftName1 = this.form.get('deliveryAddress.salutation')?.value?.trim() + ' ' + deliveryAddress.gesoftName1;
            }
            deliveryAddress.gesoftName2 = '';
        }
        deliveryAddress.gesoftName1 = (deliveryAddress.gesoftName1?.length ?? 0) > 100 ? deliveryAddress.gesoftName1?.substring(0, 100) : deliveryAddress.gesoftName1 ?? undefined;
        deliveryAddress.gesoftName2 = (deliveryAddress.gesoftName2?.length ?? 0) > 100 ? deliveryAddress.gesoftName2?.substring(0, 100) : deliveryAddress.gesoftName2 ?? undefined;

        let customer: ApiCustomersSetRequest['params']['data'] = {
            email: this.form.get('email')?.value,
            dynamicArticleIds: this.form.get('dynamicArticleIds')?.value,
            addresses: []
        }
        customer.addresses.push(billingAddress);
        if (this.isDeliveryAddress) {
            customer.addresses.push(deliveryAddress);
        }

        if (this.isGuest) {
            let priceGroup = '1';
            this.store.select(state => state.config.defaultPriceGroup).pipe(take(1)).subscribe(r => {
                console.log(r);
                if (r) {
                    priceGroup = r;
                }
            })
            customer.priceGroup = priceGroup;
            let mappedCustomer: MappedCustomer = {
                ...customer,
                id: uuidv4(),
                guest: true
            }
            this.store.dispatch(setSelectedBillingAddress({
                customerId: mappedCustomer.id,
                addressId: billingAddress.addressId ?? ''
            }));
            if (this.isDeliveryAddress) {
                this.store.dispatch(setSelectedDeliveryAddress({
                    customerId: mappedCustomer.id,
                    addressId: deliveryAddress.addressId ?? ''
                }));
            }
            this.store.dispatch(setMappedCustomersSuccess({mappedCustomers: [mappedCustomer]}));
            this.setRegisterSuccess();
            this.setLoginSuccess();

        } else {
            customer.status = 'new';
            customer.user = {
                username: this.form.get('email')?.value,
                password: this.form.get('password')?.value
            }
            customer.visibilityGroups = this.configService.getVisibilityGroups();
            this.customerService.set(customer)
                .subscribe(
                    {
                        next: (r) => {

                            if (r.error) {
                                this.formStatus = 'submitForm';
                                this.apiError = {notification: r.error.message};
                                if (r.error.input) {
                                    this.apiError.errors = r.error.input;
                                    this.apiError.errors.map(
                                        r => {
                                            if (r.key == 'params.data.user.username') {
                                                r.key = 'email';
                                                r.error = 'Die E-Mail-Adresse ' + this.form.get('email')?.value + ' existiert in unserem System bereits.';
                                            }
                                        }
                                    )
                                }
                                this.myFormReference.nativeElement.scrollIntoView({behavior: 'smooth'});
                            } else {
                                this.store.dispatch(login({
                                    username: customer.email,
                                    password: this.form.get('password')?.value
                                }));
                                this.store.select(getSelectedCustomer)
                                    .pipe(take(2))
                                    .subscribe(
                                        s => {
                                            if (s && s.id) {
                                                if (this.isDeliveryAddress && deliveryAddress.addressId) {
                                                    this.store.dispatch(setSelectedDeliveryAddress({
                                                        customerId: r.result?.customers.data.id ?? '',
                                                        addressId: deliveryAddress.addressId
                                                    }));
                                                }
                                                if (billingAddress.addressId) {
                                                    this.store.dispatch(setSelectedBillingAddress({
                                                        customerId: r.result?.customers.data.id ?? '',
                                                        addressId: billingAddress.addressId
                                                    }));
                                                }
                                                this.setLoginSuccess();
                                            }
                                        }
                                    )
                                this.setRegisterSuccess();
                            }

                        },
                        complete: () => {
                            //this.formStatus = 'submitForm';
                        },
                        error: () => {
                            this.formStatus = 'submitForm';
                        }
                    }
                )
        }
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    protected readonly faCalendar = faCalendar;

}
